import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import * as z from "zod";
import Layout from "../components/Layout";
import Hero from "../components/Hero";
import PageSeoContent from "../components/PageSeoContent";
import { SuccessResponse } from "../components/form";
import { usePost } from "../hooks/usePost";
import { HOUSE_PARTIES } from "../services/apis";

const HOUSE_PARTY_CITIES = ["Mumbai"];

const SERVICE_OPTIONS = [
  { key: "food", label: "Food" },
  { key: "sheesha", label: "Seesha" },
  { key: "beverages", label: "Beverages (Cocktails & mocktails)" },
  { key: "singerDj", label: "Singer / DJ" },
];

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  city: "Mumbai",
  address: "",
  partyDateTime: "",
  pax: "",
  occasion: "",
  services: [],
  message: "",
};

const validationSchema = z.object({
  name: z.string().min(2, { message: "Name is required" }),
  email: z.string().email({ message: "Enter a valid email address" }),
  phone: z
    .string()
    .regex(/^\d+$/, { message: "Phone number must contain only digits" })
    .length(10, { message: "Phone number must be exactly 10 digits" }),
  city: z.string().min(2, { message: "City is required" }),
  address: z.string().min(5, { message: "Address is required" }),
  partyDateTime: z.string().refine((val) => {
    if (!val) return false;
    return new Date(val) > new Date();
  }, { message: "Party date and time must be in the future" }),
  pax: z
    .string()
    .regex(/^\d+$/, { message: "PAX must be a number" })
    .refine((val) => Number(val) > 0, { message: "At least 1 guest is required" }),
  occasion: z.string().min(2, { message: "Occasion is required" }),
  services: z
    .array(z.string())
    .min(1, { message: "Select at least one service" }),
  message: z.string().optional(),
});

const HouseParties = () => {
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isValid, setIsValid] = useState(false);
  const [response, setResponse] = useState(null);

  const { mutate, isPending } = usePost({
    url: `/${HOUSE_PARTIES}`,
    onSuccess: (res) => {
      setResponse(res);
      setFormData(emptyForm);
      setErrors({});
      setTouched({});
    },
    onError: () => {},
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setIsValid(validationSchema.safeParse(formData).success);
  }, [formData]);

  const handleBlur = (name) => {
    const fieldSchema = validationSchema.shape[name];
    if (!fieldSchema) return;

    const result = fieldSchema.safeParse(formData[name] ?? "");
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({
      ...prev,
      [name]: result.success
        ? undefined
        : result.error.issues?.[0]?.message || "Invalid input",
    }));
  };

  const handleChange = (name) => (event) => {
    setFormData((prev) => ({ ...prev, [name]: event.target.value }));
  };

  const handleToggle = (field) => (key) => {
    setFormData((prev) => {
      const selected = prev[field].includes(key)
        ? prev[field].filter((item) => item !== key)
        : [...prev[field], key];
      return { ...prev, [field]: selected };
    });
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const result = validationSchema.safeParse(formData);

    if (!result.success) {
      const fieldErrors = {};
      result.error.issues.forEach((issue) => {
        fieldErrors[issue.path[0]] = issue.message;
      });
      setErrors(fieldErrors);
      setTouched({
        name: true,
        email: true,
        phone: true,
        city: true,
        address: true,
        partyDateTime: true,
        pax: true,
        occasion: true,
        services: true,
      });
      toast.error("Please fix the validation errors!");
      return;
    }

    mutate({
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      city: formData.city,
      address: formData.address.trim(),
      partyDateTime: formData.partyDateTime,
      pax: Number(formData.pax),
      occasion: formData.occasion.trim(),
      services: formData.services,
      message: formData.message.trim() || undefined,
    });
  };

  return (
    <Layout>
      <Hero title="House Parties" />
      <PageSeoContent variant="houseParties" />
      <section className="house-parties-sec section-gap">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="house-parties-card">
                <div className="head-title mb-4">
                  <div className="title-sec">
                    <h2 className="title-25">House Parties</h2>
                  </div>
                  <p>
                    Tell us about your party and the services you need. Our team
                    will get back to you.
                  </p>
                </div>

                {response ? (
                  <SuccessResponse
                    message={
                      response.message ||
                      "Thanks! Your house party request was submitted."
                    }
                  />
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-md-6 col-12 mb-3">
                        <label htmlFor="name">Name</label>
                        <input
                          id="name"
                          className="form-control"
                          type="text"
                          value={formData.name}
                          onChange={handleChange("name")}
                          onBlur={() => handleBlur("name")}
                          placeholder="Your name"
                        />
                        {touched.name && errors.name ? (
                          <p className="text-danger text-sm">{errors.name}</p>
                        ) : null}
                      </div>
                      <div className="col-md-6 col-12 mb-3">
                        <label htmlFor="email">Email</label>
                        <input
                          id="email"
                          className="form-control"
                          type="email"
                          value={formData.email}
                          onChange={handleChange("email")}
                          onBlur={() => handleBlur("email")}
                          placeholder="Email"
                        />
                        {touched.email && errors.email ? (
                          <p className="text-danger text-sm">{errors.email}</p>
                        ) : null}
                      </div>
                      <div className="col-md-6 col-12 mb-3">
                        <label htmlFor="phone">Phone Number</label>
                        <input
                          id="phone"
                          className="form-control"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange("phone")}
                          onBlur={() => handleBlur("phone")}
                          placeholder="10 digit mobile number"
                          maxLength={10}
                        />
                        {touched.phone && errors.phone ? (
                          <p className="text-danger text-sm">{errors.phone}</p>
                        ) : null}
                      </div>
                      <div className="col-md-6 col-12 mb-3">
                        <label htmlFor="city">City</label>
                        <select
                          id="city"
                          className="form-control"
                          value={formData.city}
                          onChange={handleChange("city")}
                          onBlur={() => handleBlur("city")}
                        >
                          {HOUSE_PARTY_CITIES.map((city) => (
                            <option key={city} value={city}>
                              {city}
                            </option>
                          ))}
                        </select>
                        {touched.city && errors.city ? (
                          <p className="text-danger text-sm">{errors.city}</p>
                        ) : null}
                      </div>
                      <div className="col-12 mb-3">
                        <label htmlFor="address">Address</label>
                        <input
                          id="address"
                          className="form-control"
                          type="text"
                          value={formData.address}
                          onChange={handleChange("address")}
                          onBlur={() => handleBlur("address")}
                          placeholder="Party address"
                        />
                        {touched.address && errors.address ? (
                          <p className="text-danger text-sm">{errors.address}</p>
                        ) : null}
                      </div>
                      <div className="col-md-6 col-12 mb-3">
                        <label htmlFor="partyDateTime">
                          Date & Time of the Party
                        </label>
                        <input
                          id="partyDateTime"
                          className="form-control"
                          type="datetime-local"
                          value={formData.partyDateTime}
                          onChange={handleChange("partyDateTime")}
                          onBlur={() => handleBlur("partyDateTime")}
                        />
                        {touched.partyDateTime && errors.partyDateTime ? (
                          <p className="text-danger text-sm">
                            {errors.partyDateTime}
                          </p>
                        ) : null}
                      </div>
                      <div className="col-md-6 col-12 mb-3">
                        <label htmlFor="pax">PAX</label>
                        <input
                          id="pax"
                          className="form-control"
                          type="number"
                          min="1"
                          value={formData.pax}
                          onChange={handleChange("pax")}
                          onBlur={() => handleBlur("pax")}
                          placeholder="Number of guests"
                        />
                        {touched.pax && errors.pax ? (
                          <p className="text-danger text-sm">{errors.pax}</p>
                        ) : null}
                      </div>
                      <div className="col-12 mb-3">
                        <label htmlFor="occasion">Occasion</label>
                        <input
                          id="occasion"
                          className="form-control"
                          type="text"
                          value={formData.occasion}
                          onChange={handleChange("occasion")}
                          onBlur={() => handleBlur("occasion")}
                          placeholder="e.g. Birthday, Anniversary, Housewarming"
                        />
                        {touched.occasion && errors.occasion ? (
                          <p className="text-danger text-sm">{errors.occasion}</p>
                        ) : null}
                      </div>
                    </div>

                    <h3 className="title-20 mb-3 mt-2">Services Required</h3>
                    <div className="house-parties-tags mb-3">
                      {SERVICE_OPTIONS.map((service) => (
                        <label
                          key={service.key}
                          className={`house-parties-tag ${
                            formData.services.includes(service.key)
                              ? "is-selected"
                              : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.services.includes(service.key)}
                            onChange={() => handleToggle("services")(service.key)}
                          />
                          <span>{service.label}</span>
                        </label>
                      ))}
                    </div>
                    {touched.services && errors.services ? (
                      <p className="text-danger text-sm">{errors.services}</p>
                    ) : null}

                    <h3 className="title-20 mb-3 mt-2">Additional Requests</h3>
                    <div className="mb-4">
                      <label htmlFor="message">Message</label>
                      <textarea
                        id="message"
                        className="form-control"
                        rows={4}
                        value={formData.message}
                        onChange={handleChange("message")}
                        placeholder="Anything else we should know?"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={!isValid || isPending}
                      className={`btn btn-dark ${
                        !isValid || isPending ? "opacity-60 cursor-not-allowed" : ""
                      }`}
                    >
                      {isPending ? "Submitting..." : "Submit"}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      <ToastContainer />
    </Layout>
  );
};

export default HouseParties;
